import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import BannerCard from "./BannerCard";
import AddBannerModal from "./AddBannerModal";
import EditBannerModal from "./EditBannerModal";
import DeleteConfirmModal from "./DeleteConfirmModal";

import {
  getBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "../../../../api/apiServices";

export default function BannersGrid() {
  const [banners, setBanners] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [deletingBanner, setDeletingBanner] = useState(null);
  const [loading, setLoading] = useState(false);

  /* -------------------- DND SENSORS -------------------- */
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /* -------------------- LOAD BANNERS -------------------- */
  const loadBanners = async () => {
    try {
      setLoading(true);
      const res = await getBanners();

      // ensure consistent id usage
      const formatted = (res || []).map((b, index) => ({
        ...b,
        id: b._id,
        order: b.order || index + 1,
      }));

      setBanners(formatted);
    } catch (err) {
      console.error("Failed to load banners:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  /* -------------------- DRAG END -------------------- */
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = banners.findIndex((b) => b.id === active.id);
    const newIndex = banners.findIndex((b) => b.id === over.id);

    const reordered = arrayMove(banners, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        order: index + 1,
      })
    );

    setBanners(reordered);

    // OPTIONAL: call backend to save order
    // await updateBannerOrder(reordered)
  };

  /* -------------------- ADD -------------------- */
  const handleAddBanner = async (formData) => {
    try {
      await createBanner(formData);
      setIsAddModalOpen(false);
      loadBanners();
    } catch (err) {
      console.error("Add banner error:", err);
    }
  };

  /* -------------------- EDIT -------------------- */
  const handleEditBanner = async ({ id, formData }) => {
    try {
      await updateBanner(id, formData);
      setEditingBanner(null);
      loadBanners();
    } catch (err) {
      console.error("Update banner error:", err);
    }
  };

  /* -------------------- DELETE -------------------- */
  const handleDeleteBanner = async () => {
    if (!deletingBanner) return;

    try {
      await deleteBanner(deletingBanner.id);
      setDeletingBanner(null);
      loadBanners();
    } catch (err) {
      console.error("Delete banner error:", err);
    }
  };

  /* -------------------- TOGGLE ACTIVE -------------------- */
  const handleToggleEnabled = async (banner) => {
    try {
      await updateBanner(banner.id, {
        isActive: !banner.isActive,
      });

      loadBanners();
    } catch (err) {
      console.error("Toggle error:", err);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            Banner Slider Images
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Drag to reorder banners
          </p>
        </div>

        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#EF579A] text-white px-5 py-2.5 rounded-lg hover:bg-[#d94a87]"
        >
          Add Banner
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={banners.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {banners.map((banner) => (
                  <BannerCard
                    key={banner.id}
                    banner={banner}
                    onEdit={() => setEditingBanner(banner)}
                    onDelete={() => setDeletingBanner(banner)}
                    onToggleEnabled={() => handleToggleEnabled(banner)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {banners.length === 0 && (
            <div className="text-center py-16 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-600 mb-4">No banners yet</p>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-[#EF579A] text-white px-6 py-2.5 rounded-lg"
              >
                Add Your First Banner
              </button>
            </div>
          )}
        </>
      )}

      {/* -------------------- MODALS -------------------- */}

      <AddBannerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddBanner}
      />

      {editingBanner && (
        <EditBannerModal
          isOpen={true}
          banner={editingBanner}
          onClose={() => setEditingBanner(null)}
          onSave={handleEditBanner}
        />
      )}

      {deletingBanner && (
        <DeleteConfirmModal
          isOpen={true}
          bannerTitle={deletingBanner.title}
          onClose={() => setDeletingBanner(null)}
          onConfirm={handleDeleteBanner}
        />
      )}
    </div>
  );
}
